// Edge-case helpers consumed by emitSource: comment-region detection,
// line-ending detection + normalization, indent-style detection,
// quoting-style detection + application, and trailing-semicolon location.
//
// Engine-agnostic: the helpers operate on the universal subset of SQL
// quoting/comment shapes (`/* */`, `--`, `'…'`, `"…"`, `` `…` ``, `[…]`,
// `$tag$…$tag$`). Engine-specific extensions (e.g. `#` line comments in
// MySQL) are accepted as additional comment kinds.
//
// PRD: Ralph/SemanticResolverEdit/PRD.md § Slice: emitSource — edge cases.

import type { SqlEngine } from '../../sql-grammar/model';

export type LineEnding = '\n' | '\r\n';

export type CommentKind = 'block' | 'line';

export interface CommentRegion {
  readonly start: number;
  readonly end: number;
  readonly kind: CommentKind;
}

export type QuotingStyle = 'bracket' | 'backtick' | 'doubleQuote' | 'unquoted';

// --- line-ending detection ---------------------------------------------------

// Detect the dominant line ending in `text`. Counts CRLF vs lone LF and
// returns the majority (defaulting to '\n' when there are no line breaks).
export function detectLineEnding(text: string): LineEnding {
  let crlf = 0;
  let lf = 0;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\n') {
      if (text[i - 1] === '\r') crlf++;
      else lf++;
    }
  }
  return crlf > lf ? '\r\n' : '\n';
}

// Rewrite every `\r?\n` in `text` to `eol`. Used by emitSource to normalize
// inserted-text line endings to the buffer's dominant style.
export function normalizeLineEndings(text: string, eol: LineEnding): string {
  // Convert all to LF first, then to eol — guards against mixed input.
  const lf = text.replace(/\r\n/g, '\n');
  if (eol === '\n') return lf;
  return lf.replace(/\n/g, '\r\n');
}

// --- indent detection --------------------------------------------------------

// Inspect the leading whitespace of indented lines and return the dominant
// indent unit. Falls back to two spaces when the buffer has no indented lines.
export function detectIndentUnit(text: string): string {
  const counts = { tab: 0, two: 0, four: 0 } as Record<string, number>;
  let lineStart = true;
  for (let i = 0; i < text.length; i++) {
    if (text[i] === '\n') { lineStart = true; continue; }
    if (!lineStart) continue;
    lineStart = false;
    if (text[i] === '\t') { counts.tab++; continue; }
    if (text[i] !== ' ') continue;
    let spaces = 0;
    while (text[i + spaces] === ' ' && spaces < 16) spaces++;
    if (spaces >= 4) counts.four++;
    else if (spaces >= 2) counts.two++;
  }
  if (counts.tab >= counts.two && counts.tab >= counts.four && counts.tab > 0) return '\t';
  if (counts.four >= counts.two && counts.four > 0) return '    ';
  if (counts.two > 0) return '  ';
  return '  ';
}

// Read the leading whitespace + indent on the line that contains `offset`.
// Returns the byte slice between the line start and `offset`. Stops at the
// first non-whitespace byte. Useful when synthesising a multi-line replacement
// that should sit at the same indent level as the byte at `offset`.
export function leadingIndentAtOffset(source: string, offset: number): string {
  let lineStart = offset;
  while (lineStart > 0 && source[lineStart - 1] !== '\n') lineStart--;
  let i = lineStart;
  while (i < source.length && (source[i] === ' ' || source[i] === '\t')) i++;
  return source.slice(lineStart, i);
}

// --- quoting-style detection -------------------------------------------------

// Identify the quoting style of an identifier token (or relation reference).
// `unquoted` is returned when the token is not wrapped in any of the
// supported quote pairs.
export function detectQuotingStyle(token: string): QuotingStyle {
  if (token.length < 2) return 'unquoted';
  const first = token[0];
  const last = token[token.length - 1];
  if (first === '[' && last === ']') return 'bracket';
  if (first === '`' && last === '`') return 'backtick';
  if (first === '"' && last === '"') return 'doubleQuote';
  return 'unquoted';
}

// Strip the surrounding quotes of an identifier token, undoing the doubled-
// quote escape that `'…''…'`-style identifiers use. Returns the raw name.
export function unquoteIdentifier(token: string): string {
  const style = detectQuotingStyle(token);
  if (style === 'unquoted') return token;
  const inner = token.slice(1, -1);
  if (style === 'doubleQuote') return inner.replace(/""/g, '"');
  if (style === 'backtick') return inner.replace(/``/g, '`');
  return inner; // bracket: MSSQL allows `]]` to escape; rare, skipped here.
}

// Wrap `name` with the requested quoting style. Doubles any embedded quote
// character to escape it (matches MSSQL/PG/MySQL idioms). Bracket uses `]]`
// per the MSSQL convention.
export function applyQuotingStyle(name: string, style: QuotingStyle): string {
  if (style === 'unquoted') return name;
  if (style === 'doubleQuote') return `"${name.replace(/"/g, '""')}"`;
  if (style === 'backtick') return `\`${name.replace(/`/g, '``')}\``;
  return `[${name.replace(/]/g, ']]')}]`;
}

// --- comment-region detection ------------------------------------------------

// Walk `source` from offset 0 and find the comment region (if any) that
// strictly contains `offset` (i.e. `start < offset < end`). Returns null
// when `offset` sits in code, in a string literal, in a quoted identifier,
// or AT a comment boundary (start or end). Boundary positions are valid
// splice points: an insert at `comment.start` lands BEFORE the comment;
// an insert at `comment.end` lands AFTER it.
//
// Engine-agnostic: tracks `'…'`, `"…"`, `` `…` ``, `[…]`, `$tag$…$tag$` as
// non-comment skip regions, plus `--` line comments (terminated by `\n`) and
// `/* … */` block comments (no nesting per the SQL standard).
//
// `#` is a line-comment opener ONLY in MySQL — pass `engine === 'mysql'` to
// recognise it. On every other engine (MSSQL/PG/Oracle/SQLite) `#` is a
// normal character (e.g. MSSQL temp tables `#tmp` / `##global`), so the
// default (engine undefined or non-mysql) treats `#` as code. This mirrors
// the `#`-as-comment gating used by classify/lexicalState.ts, clauseScanner.ts
// and tokenSkip.ts.
//
// eslint-disable-next-line complexity
export function findEnclosingComment(source: string, offset: number, engine?: SqlEngine): CommentRegion | null {
  if (offset < 0 || offset > source.length) return null;
  let i = 0;
  while (i < source.length) {
    const c = source[i];
    const c2 = source[i + 1] ?? '';
    // Block comment.
    if (c === '/' && c2 === '*') {
      const start = i;
      let j = i + 2;
      while (j < source.length && !(source[j] === '*' && source[j + 1] === '/')) j++;
      const end = Math.min(source.length, j + 2);
      if (offset > start && offset < end) return { start, end, kind: 'block' };
      i = end;
      continue;
    }
    // Line comment (`--` always; `#` only in MySQL).
    if ((c === '-' && c2 === '-') || (c === '#' && engine === 'mysql')) {
      const start = i;
      let j = c === '#' ? i + 1 : i + 2;
      while (j < source.length && source[j] !== '\n') j++;
      const end = j;
      if (offset > start && offset < end) return { start, end, kind: 'line' };
      i = end;
      continue;
    }
    // String / quoted-id skip.
    if (c === "'") { i = skipQuoted(source, i, "'"); continue; }
    if (c === '"') { i = skipQuoted(source, i, '"'); continue; }
    if (c === '`') { i = skipPair(source, i, '`'); continue; }
    if (c === '[') { i = skipPair(source, i, ']'); continue; }
    if (c === '$') {
      const m = source.slice(i).match(/^\$([A-Za-z_][A-Za-z0-9_]*)?\$/);
      if (m) {
        const tag = m[0];
        let j = i + tag.length;
        while (j < source.length && !source.startsWith(tag, j)) j++;
        i = Math.min(source.length, j + tag.length);
        continue;
      }
    }
    i++;
  }
  return null;
}

function skipQuoted(source: string, start: number, quote: string): number {
  let j = start + 1;
  while (j < source.length) {
    if (source[j] === quote) {
      if (source[j + 1] === quote) { j += 2; continue; }
      return j + 1;
    }
    j++;
  }
  return source.length;
}

function skipPair(source: string, start: number, closer: string): number {
  let j = start + 1;
  while (j < source.length && source[j] !== closer) j++;
  return Math.min(source.length, j + 1);
}

// --- trailing semicolon ------------------------------------------------------

// Locate the trailing `;` for a statement that ends at `endOffset` (exclusive).
// Returns the offset of the semicolon when present, or `null` when the
// statement isn't semicolon-terminated. Whitespace between the statement and
// the semicolon is allowed.
export function findTrailingSemicolon(source: string, endOffset: number): number | null {
  let i = endOffset - 1;
  while (i >= 0 && /\s/.test(source[i])) i--;
  if (i < 0) return null;
  if (source[i] === ';') return i;
  return null;
}
